(() => {
  if (customElements.get('subscription-product')) return;

  const CHECKFREQ_URL = 'https://subscriptions.gosimple.app/api/checkfreq';
  // South Africa has no daylight saving, so SAST is UTC+2 all year.
  const SAST_OFFSET = 2 * 60 * 60 * 1000;
  const HOUR = 60 * 60 * 1000;
  const DAY = 24 * HOUR;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const toCents = (amount) => Math.round(parseFloat(amount) * 100);
  const normalise = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  // The store's money format is "R {{amount}}"; the mock-up prints prices as "R700.00", without the space.
  const tightMoney = (text) => String(text).replace(/^([^\d\s-]+)\s+(?=[\d-])/, '$1');

  // Same reading of GoSimple's checkfreq response as hw-subs.js, so both widgets agree.
  const checkfreqAllowed = (raw) => {
    const text = normalise(raw);
    if (text === '0' || text === 'false') return false;
    if (text === '1' || text === 'true') return true;
    try {
      const json = JSON.parse(raw);
      if (json && (json.allowed === false || json.ok === false)) return false;
    } catch (error) {
      // Not JSON: fall through to the non-empty check.
    }
    return text !== '';
  };

  const userError = (message) => Object.assign(new Error(message), { userMessage: message });

  /* Billing cycle maths. Date.UTC rolls month and day overflow, so month 12 or day 0 just work. */

  const saMidnight = (year, month, day) => Date.UTC(year, month, day) - SAST_OFFSET;

  const billDayIn = (year, month, billDay) => {
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    let day = Math.min(billDay, lastDay);
    const weekday = new Date(Date.UTC(year, month, day)).getUTCDay();
    // Debit orders don't run on weekends, so a Saturday or Sunday bill day moves back to Friday.
    if (weekday === 6) day -= 1;
    if (weekday === 0) day -= 2;
    return day;
  };

  const nextCycle = (now, billDay, shipDay, arrivalDays) => {
    const today = new Date(now + SAST_OFFSET);
    const year = today.getUTCFullYear();
    let month = today.getUTCMonth();

    for (let i = 0; i < 2; i += 1) {
      const bill = billDayIn(year, month, billDay);
      const closes = saMidnight(year, month, bill);
      if (now < closes) {
        return {
          closes,
          // Order-by is the billing day itself: you can order right up to when the box is billed on the 28th.
          orderBy: saMidnight(year, month, bill),
          roasted: saMidnight(year, month + 1, shipDay - 1),
          ships: saMidnight(year, month + 1, shipDay),
          arrives: saMidnight(year, month + 1, shipDay + arrivalDays),
        };
      }
      month += 1;
    }
    return null;
  };

  class SubscriptionProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json || this.product) return;

      this.product = JSON.parse(json.textContent);
      this.form = this.querySelector('[data-form]');
      this.cta = this.querySelector('[data-cta]');
      this.ctaLabel = this.querySelector('[data-cta-label]');
      this.ctaPrice = this.querySelector('[data-cta-price]');
      this.errorEl = this.querySelector('[data-error]');
      this.subCard = this.querySelector('[data-plan-card="subscription"]');
      this.oneoffCard = this.querySelector('[data-plan-card="oneoff"]');
      this.termGroup = this.querySelector('[data-term-group]');
      this.giftPanel = this.querySelector('[data-gift-panel]');
      this.giftMessage = this.querySelector('[data-gift-message]');
      this.addedDialog = this.querySelector('[data-added]');
      this.nudge = this.querySelector('[data-nudge]');
      this.giftLengthIndex = this.dataset.giftLengthIndex ? Number(this.dataset.giftLengthIndex) : -1;

      this.planCache = new Map();
      this.plans = [];
      this.plan = null;
      this.plansLoading = true;
      this.preferSubscription = true;
      this.requestId = 0;
      this.busy = false;

      // Listened for on the whole element: in GoSimple mode the option pills sit outside the (fallback) form.
      this.addEventListener('change', this.onChange.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.giftMessage) this.giftMessage.addEventListener('input', () => this.renderGiftCount());

      this.querySelectorAll('[data-open-modal]').forEach((button) => {
        button.addEventListener('click', () => {
          const dialog = document.getElementById(button.dataset.openModal);
          if (dialog && !dialog.open) dialog.showModal();
        });
      });
      this.querySelectorAll('.sub-pdp__modal').forEach((dialog) => {
        dialog.addEventListener('click', (event) => {
          // The dialog itself has no padding, so a click that lands on it is a backdrop click.
          if (event.target === dialog || event.target.closest('[data-close-modal]')) dialog.close();
        });
      });
      if (this.addedDialog) {
        this.addedDialog.querySelector('[data-view-cart]').addEventListener('click', () => this.viewCart());
      }

      this.initGallery();
      this.initCrate();
      this.initTimeline();
      this.initNudge();
      this.renderGiftCount();
      this.initGoSimple();
      this.preselectDefaults();
      this.update(false);
      this.updateFromPrice();
    }

    disconnectedCallback() {
      window.clearInterval(this.timelineTimer);
      if (this.onNudgeScroll) window.removeEventListener('scroll', this.onNudgeScroll);
      window.cancelAnimationFrame(this.nudgeFrame);
      if (this.gsObserver) this.gsObserver.disconnect();
      if (this.gsPriceObserver) this.gsPriceObserver.disconnect();
      window.clearTimeout(this.gsTimer);
      if (this.onGoSimpleIntent) {
        window.removeEventListener('click', this.onGoSimpleIntent, true);
        window.removeEventListener('submit', this.onGoSimpleIntent, true);
      }
    }

    get isGift() {
      return this.dataset.mode === 'gift';
    }

    get months() {
      if (!this.isGift) return 1;
      const input = this.querySelector('[data-term-input]:checked') || this.querySelector('[data-term-input]');
      if (!input) return 1;
      input.checked = true;
      return parseInt(input.value, 10) || 1;
    }

    // Gift length is a real product option, picked through the term pills and the two pay cards.
    giftLengthValue() {
      if (this.selectedPlan === 'oneoff') return this.dataset.giftOneoffValue || null;
      const input = this.querySelector('[data-term-input]:checked') || this.querySelector('[data-term-input]');
      return input ? input.value : null;
    }

    get selectedPlan() {
      // Once the GoSimple widget is in charge, its own plan cards are the source of truth.
      if (this.gosimpleReady) return this.gsWidget.dataset.purchaseMode === 'subscribe' ? 'subscription' : 'oneoff';
      const input = this.querySelector('[data-plan-input]:checked');
      return input ? input.value : 'oneoff';
    }

    get routes() {
      return {
        cart: (window.routes && window.routes.cart_url) || '/cart',
        cartAdd: (window.routes && window.routes.cart_add_url) || '/cart/add',
      };
    }

    checkedValue(role) {
      const group = this.querySelector(`[data-option-role="${role}"]`);
      if (!group || group.hidden) return null;
      const input = group.querySelector('[data-option-input]:checked');
      return input ? input.value : null;
    }

    onChange(event) {
      const target = event.target;
      this.showError('');

      if (target.matches('[data-option-input]')) {
        this.querySelectorAll('.is-missing').forEach((group) => group.classList.remove('is-missing'));
        // Switching format can invalidate the current size (3 bags -> pods), leaving that group empty.
        // Re-preselect so the box stays full with the default for the new format, as it is on load.
        this.preselectDefaults();
        this.update(true);
      } else if (target.matches('[data-plan-input]')) {
        this.preferSubscription = target.value === 'subscription';
        // For gifts the card chooses the gift length variant, so it re-resolves instead of just re-rendering.
        if (this.isGift) this.update(true);
        else this.render();
      } else if (target.matches('[data-term-input]')) {
        if (this.isGift) this.update(true);
        else this.render();
      } else if (target.matches('[data-gift-toggle]') && this.giftMessage) {
        if (this.giftPanel) this.giftPanel.hidden = !target.checked;
        this.giftMessage.disabled = !target.checked;
        if (target.checked) this.giftMessage.focus();
        this.renderGiftCount();
      }
    }

    /* Variants */

    // Walks option groups in order. A group opens once every group above it has a pick, so dependent
    // options (bag sizes vs pouch sizes) never show before the format that decides them.
    syncOptions() {
      const chosen = [];
      if (this.isGift && this.giftLengthIndex >= 0) chosen[this.giftLengthIndex] = this.giftLengthValue();
      const keyword = normalise(this.dataset.podsKeyword || 'pod');
      let priorComplete = true;
      let formatValue = null;

      this.querySelectorAll('[data-option-group], [data-property-group]').forEach((group) => {
        const inputs = [...group.querySelectorAll('[data-option-input]')];

        // Order properties (grind on the gift box) don't pick a variant, but still wait their turn, and pods skip them.
        if (group.hasAttribute('data-property-group')) {
          const skipped = Boolean(keyword && normalise(formatValue).includes(keyword));
          if (skipped) inputs.forEach((input) => { input.checked = false; });
          group.hidden = !priorComplete || skipped;
          group.toggleAttribute('data-pending', !priorComplete && !skipped);
          if (priorComplete && !skipped && !inputs.some((input) => input.checked)) priorComplete = false;
          return;
        }

        const index = Number(group.dataset.optionIndex);
        const available = inputs.filter((input) => {
          const exists = this.product.variants.some(
            (variant) =>
              variant.options[index] === input.value &&
              chosen.every((value, i) => value == null || variant.options[i] === value)
          );
          input.closest('.sub-pdp__pill').hidden = !exists;
          if (!exists) input.checked = false;
          return exists;
        });

        // One possible value (e.g. grind once pods are chosen) isn't a choice, so pick it and step aside.
        if (priorComplete && available.length === 1) available[0].checked = true;
        const checked = available.find((input) => input.checked);

        group.hidden = !priorComplete || available.length <= 1;
        group.toggleAttribute('data-pending', !priorComplete && available.length > 1);
        chosen[index] = priorComplete && checked ? checked.value : null;
        if (group.dataset.optionRole === 'format') formatValue = chosen[index];
        if (chosen[index] == null) priorComplete = false;
      });

      this.chosenOptions = chosen;
      return chosen;
    }

    // Opens the configurator with a full box already picked, so every option group is visible at once
    // (Beans, the most-popular size, first grind) instead of revealing one group at a time. Skips any
    // group already answered by a ?variant deep link, and re-syncs between picks so dependent values
    // (bag vs pouch sizes) are resolved against the format chosen a moment earlier.
    preselectDefaults() {
      this.querySelectorAll('[data-option-group], [data-property-group]').forEach((group) => {
        this.syncOptions();
        // Skip groups the current format doesn't use (e.g. grind is hidden for pods) so we don't pick a phantom value.
        if (group.hidden) return;
        const inputs = [...group.querySelectorAll('[data-option-input]')].filter(
          (input) => !input.closest('.sub-pdp__pill').hidden
        );
        if (!inputs.length || inputs.some((input) => input.checked)) return;
        const popular = inputs.find((input) => input.closest('.sub-pdp__pill').querySelector('.sub-pdp__flag'));
        const pick = group.dataset.optionRole === 'size' && popular ? popular : inputs[0];
        pick.checked = true;
      });
    }

    // The real variant only exists once every option is picked; until then prices preview the cheapest match.
    resolveVariant() {
      const variants = this.product.variants;
      const chosen = Array.from(this.syncOptions());
      if (!chosen.length) return { variant: variants[0] || null, preview: null };

      const matches = variants.filter((variant) => chosen.every((value, i) => value == null || variant.options[i] === value));
      const variant = chosen.every((value) => value != null) ? matches[0] || null : null;
      const pool = matches.length ? matches : variants;
      const preview = pool.reduce((min, candidate) => (candidate.price < min.price ? candidate : min), pool[0]) || null;
      return { variant, preview };
    }

    async update(fromUser) {
      const { variant, preview } = this.resolveVariant();
      this.variant = variant;
      this.displayVariant = variant || preview;

      if (fromUser) {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.toString());
        if (variant && variant.featured_media) this.showSlide(variant.featured_media.position - 1);
      }

      this.syncModalRows();
      this.syncGoSimple();
      const target = this.isGift ? null : this.displayVariant;
      this.plans = [];
      this.plansLoading = Boolean(target);
      this.render();
      if (!target) return;

      const requestId = ++this.requestId;
      const plans = await this.fetchPlans(target.id);
      if (requestId !== this.requestId) return;

      this.plans = plans;
      this.plansLoading = false;
      this.render();
    }

    /* GoSimple widget (app block) */

    get gosimpleReady() {
      return this.dataset.gosimple === 'ready';
    }

    // The button the shopper actually sees: GoSimple's once it's in charge, otherwise ours.
    get activeCta() {
      if (this.gosimpleReady) {
        return (
          this.gsWidget.querySelector('.gosimple-layout:not([hidden]) .gosimple-add-to-cart-btn') ||
          this.gsWidget.querySelector('.gosimple-add-to-cart-btn') ||
          this.cta
        );
      }
      return this.cta;
    }

    initGoSimple() {
      const slot = this.querySelector('[data-gosimple-slot]');
      this.gsWidget = slot && slot.querySelector('.gosimple-subscription-widget--root');
      if (!slot) return;
      if (!this.gsWidget) {
        this.setGoSimpleState('fallback');
        return;
      }

      this.gsVariantInput = this.gsWidget.querySelector('.gosimple-variant-id');
      this.placeGoSimpleCopy();

      // GoSimple rewrites its price text whenever the variant changes, so the touch-ups are reapplied each time.
      this.gsPriceObserver = new MutationObserver(() => this.polishGoSimplePrices());
      this.gsPriceObserver.observe(this.gsWidget, { subtree: true, childList: true, characterData: true });
      this.polishGoSimplePrices();

      // The widget reports its own state on these attributes, so we follow them rather than its internals.
      this.gsObserver = new MutationObserver(() => {
        this.readGoSimpleState();
        this.render();
      });
      this.gsObserver.observe(this.gsWidget, {
        attributes: true,
        attributeFilter: ['data-has-plan', 'data-layout-ready', 'data-purchase-mode', 'data-sold-out'],
      });

      // If the app script never boots (blocked or offline), don't leave the box without a button.
      this.gsTimer = window.setTimeout(() => {
        if (this.dataset.gosimple === 'pending') this.setGoSimpleState('fallback');
      }, 8000);

      // GoSimple's storefront script takes widget clicks and submits in the capture phase on document,
      // so an unfinished box has to be caught one step earlier, on window.
      this.onGoSimpleIntent = (event) => this.onGoSimpleClick(event);
      window.addEventListener('click', this.onGoSimpleIntent, true);
      window.addEventListener('submit', this.onGoSimpleIntent, true);
      this.gsWidget.addEventListener('change', (event) => {
        if (event.target.matches('.gosimple-frequency-select-mirror, .gosimple-frequency-select')) {
          this.readGoSimpleFrequency();
          this.render();
        }
      });

      this.readGoSimpleState();
    }

    readGoSimpleState() {
      const widget = this.gsWidget;
      const wasReady = this.gosimpleReady;

      if (widget.dataset.hasPlan === 'false') {
        // GoSimple hides itself when this box has no plan; the theme's once-off card and button take over.
        this.setGoSimpleState('fallback');
      } else if (widget.dataset.hasPlan === 'true' && widget.dataset.layoutReady === 'true') {
        this.setGoSimpleState('ready');
      }

      if (this.gosimpleReady && !wasReady) {
        // The widget may have booted on its own default variant after our first sync, so line it up again.
        this.syncGoSimple();
        this.readGoSimpleFrequency();
      }
    }

    setGoSimpleState(state) {
      if (state !== 'pending') window.clearTimeout(this.gsTimer);
      this.dataset.gosimple = state;
    }

    // Keeps the cup price and reminder bar on the same delivery schedule the shopper picked in the widget.
    readGoSimpleFrequency() {
      const select =
        this.gsWidget.querySelector('.gosimple-layout:not([hidden]) .gosimple-frequency-select-mirror') ||
        this.gsWidget.querySelector('.gosimple-frequency-select');
      const option = select && select.selectedOptions[0];
      if (option && option.textContent.trim()) this.dataset.frequency = option.textContent.trim();
    }

    // Our option pills pick the variant; the widget's own dropdowns are hidden, so it's told here.
    syncGoSimple() {
      if (!this.gsWidget || this.isGift) return;
      const target = this.variant || this.displayVariant;
      const prefix = this.variant ? '' : `${this.dataset.fromLabel || 'From'} `;

      this.toggleAttribute('data-box-incomplete', !this.variant);
      // Read by CSS (attr()) so "From" shows until the box is complete, like the theme cards.
      this.gsWidget.querySelectorAll('.gosimple-discounted-price, .gosimple-onetime-price').forEach((price) => {
        price.dataset.prefix = prefix;
      });

      if (!target || !this.gsVariantInput || this.gsVariantInput.value === String(target.id)) return;
      this.gsVariantInput.value = String(target.id);
      // The widget re-reads its hidden variant input on this event (the same one it fires for its own dropdowns).
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: target } }));
    }

    // Runs for every click and submit on the page, so it bails out fast unless it's this widget's add button or form.
    onGoSimpleClick(event) {
      const target = event.target;
      if (!(target instanceof Element) || !this.gosimpleReady || !this.gsWidget.contains(target)) return;
      const isAdd = event.type === 'submit' ? target.matches('.gosimple-card') : Boolean(target.closest('.gosimple-add-to-cart-btn'));
      if (!isAdd) return;

      if (!this.variant || this.missingOptionGroups().length) {
        // Same behaviour as the theme button: point at what's still missing instead of adding a half-built box.
        event.preventDefault();
        event.stopImmediatePropagation();
        this.promptMissing();
        return;
      }
      this.showError('');
      this.retireNudge();
    }

    // Matches the mock-up's plan cards: "R700.00" (no space after the R), and the once-off price greyed out and
    // struck through beside the subscribe price. The strike lives in data-compare (drawn by CSS) because GoSimple
    // replaces the price element's text, which would wipe out a child element.
    polishGoSimplePrices() {
      const prices = '.gosimple-discounted-price, .gosimple-onetime-price, .gosimple-onetime-compare, .gosimple-btn-price, .gosimple-btn-compare';
      this.gsWidget.querySelectorAll(prices).forEach((price) => {
        const text = price.textContent.trim();
        // Only writes when something changes, so this observer's own edits don't retrigger it forever.
        if (text && tightMoney(text) !== price.textContent) price.textContent = tightMoney(text);
      });

      const cents = (el) => (el ? toCents(el.textContent.replace(/[^\d.]/g, '')) || 0 : 0);
      this.gsWidget.querySelectorAll('.sbw-plans').forEach((plans) => {
        const sub = plans.querySelector('.sbw-subscribe .gosimple-discounted-price');
        const once = plans.querySelector('.gosimple-onetime-price');
        if (!sub) return;
        const compare = once && cents(once) > cents(sub) ? once.textContent.trim() : '';
        if ((sub.dataset.compare || '') !== compare) sub.dataset.compare = compare;
      });
    }

    // The widget has no slots for the sticker, perks and once-off note, so they're copied in from the section.
    placeGoSimpleCopy() {
      const template = this.querySelector('[data-gosimple-copy]');
      if (!template) return;
      const copy = (key) => {
        const node = template.content.querySelector(`[data-copy="${key}"]`);
        return node ? node.cloneNode(true) : null;
      };
      const place = (card, keys) => {
        if (!card || card.querySelector('[data-copy]')) return;
        keys.forEach((key) => {
          const node = copy(key);
          if (node) card.appendChild(node);
        });
      };

      this.gsWidget.querySelectorAll('.sbw-subscribe').forEach((card) => place(card, ['sub-flag', 'sub-perks']));
      this.gsWidget.querySelectorAll('.sbw-once').forEach((card) => place(card, ['oneoff-perks', 'oneoff-note']));
    }

    /* GoSimple plans */

    fetchPlans(variantId) {
      if (!this.planCache.has(variantId)) {
        // GoSimple publishes each variant's plans as a theme asset (the same file hw-subs.js loads).
        const base = window.hwsAssetDummy;
        const request = base
          ? fetch(base.replace('dummy.json', `${this.dataset.productId}_${variantId}.json`))
              .then((response) => (response.ok ? response.json() : null))
              .then((result) => this.parsePlans(result))
              .catch(() => [])
          : Promise.resolve([]);
        this.planCache.set(variantId, request);
      }
      return this.planCache.get(variantId);
    }

    parsePlans(result) {
      if (!result || !result.returnhtml) return [];
      const doc = new DOMParser().parseFromString(result.returnhtml, 'text/html');
      return [...doc.querySelectorAll('input[name="subscriptionplan"]')]
        .filter((input) => input.value !== 'onetime' && input.dataset.frid)
        .map((input) => ({
          subtype: input.dataset.subtype || '',
          desc: input.dataset.desc || '',
          actual: input.dataset.actual || '',
          discount: input.dataset.discount || input.dataset.actual || '',
          frid: input.dataset.frid,
        }));
    }

    findPlan(wanted, plans = this.plans) {
      if (!normalise(wanted)) return plans[0] || null;
      return plans.find((plan) => normalise(plan.subtype) === normalise(wanted)) || null;
    }

    async updateFromPrice() {
      const target = this.querySelector('[data-from-price]');
      if (!target || !this.product.variants.length) return;

      if (this.isGift) {
        const perMonth = this.product.variants
          .map((variant) => ({ price: variant.price, months: parseInt(variant.options[this.giftLengthIndex], 10) }))
          .filter(({ months }) => months > 0)
          .map(({ price, months }) => Math.round(price / months));
        if (!perMonth.length) return;
        target.textContent = this.moneyNoZeros(Math.min(...perMonth));
        target.closest('[data-from-badge]').hidden = false;
        return;
      }

      const cheapest = this.product.variants.reduce((min, variant) => (variant.price < min.price ? variant : min));
      const plan = this.findPlan(this.dataset.frequency, await this.fetchPlans(cheapest.id));
      if (!plan) return;

      target.textContent = this.moneyNoZeros(toCents(plan.discount));
      target.closest('[data-from-badge]').hidden = false;
    }

    /* Rendering */

    render() {
      if (this.isGift) this.renderGiftPlans();
      else this.renderPlans();

      this.renderCta();
      this.renderCrate();
      this.renderNudge();
    }

    renderPlans() {
      const variant = this.displayVariant;
      const plan = this.plansLoading ? null : this.findPlan(this.dataset.frequency);
      const prefix = this.variant ? '' : `${this.dataset.fromLabel || 'From'} `;
      this.plan = plan;

      if (this.subCard) {
        const input = this.subCard.querySelector('[data-plan-input]');
        const priceEl = this.subCard.querySelector('[data-plan-price]');
        const strikeEl = this.subCard.querySelector('[data-plan-strike]');
        const unavailable = !this.plansLoading && !plan;

        this.subCard.classList.toggle('is-loading', this.plansLoading);
        this.subCard.classList.toggle('is-unavailable', unavailable);
        this.subCard.querySelector('[data-plan-unavailable]').hidden = !unavailable;
        input.disabled = unavailable;

        if (unavailable && input.checked) this.checkPlan('oneoff');
        if (plan && this.preferSubscription && !input.checked) this.checkPlan('subscription');

        if (plan && variant) {
          const total = toCents(plan.discount);
          priceEl.textContent = prefix + this.formatMoney(total);
          strikeEl.textContent = this.formatMoney(variant.price);
          strikeEl.hidden = !this.variant || variant.price <= total;
        } else {
          priceEl.textContent = '';
          strikeEl.hidden = true;
        }
      }

      if (this.oneoffCard && variant) {
        this.oneoffCard.querySelector('[data-plan-price]').textContent = prefix + this.formatMoney(variant.price);
      }
    }

    // Gifts are one-time purchases: each card prices a real variant for its gift length, no GoSimple involved.
    renderGiftPlans() {
      const prefix = this.variant ? '' : `${this.dataset.fromLabel || 'From'} `;
      const termInput = this.querySelector('[data-term-input]:checked');
      const giftVariant = this.variantForGiftLength(termInput && termInput.value);
      const oneoffVariant = this.variantForGiftLength(this.dataset.giftOneoffValue);
      this.plan = null;

      if (this.subCard) {
        const input = this.subCard.querySelector('[data-plan-input]');
        const priceEl = this.subCard.querySelector('[data-plan-price]');
        const strikeEl = this.subCard.querySelector('[data-plan-strike]');
        const noteEl = this.subCard.querySelector('[data-plan-note]');
        const compare = giftVariant && giftVariant.compare_at_price;

        this.subCard.classList.remove('is-loading');
        this.subCard.classList.toggle('is-unavailable', !giftVariant);
        this.subCard.querySelector('[data-plan-unavailable]').hidden = Boolean(giftVariant);
        input.disabled = !giftVariant;
        priceEl.textContent = giftVariant ? prefix + this.moneyNoZeros(giftVariant.price) : '';
        strikeEl.textContent = compare ? this.moneyNoZeros(compare) : '';
        strikeEl.hidden = !(compare && compare > giftVariant.price);
        if (noteEl && noteEl.dataset.template) noteEl.textContent = noteEl.dataset.template.replace('[months]', this.months);
      }

      if (this.oneoffCard) {
        this.oneoffCard.querySelector('[data-plan-input]').disabled = !oneoffVariant;
        this.oneoffCard.classList.toggle('is-unavailable', !oneoffVariant);
        this.oneoffCard.querySelector('[data-plan-price]').textContent = oneoffVariant ? prefix + this.moneyNoZeros(oneoffVariant.price) : '';
      }

      if (this.termGroup) this.termGroup.hidden = this.selectedPlan === 'oneoff';
    }

    // A gift length's price given the format and size picked so far; the cheapest match until they're picked.
    variantForGiftLength(value) {
      if (!value || this.giftLengthIndex < 0) return null;
      const chosen = Array.from(this.chosenOptions || []);
      return this.product.variants
        .filter(
          (variant) =>
            variant.options[this.giftLengthIndex] === value &&
            chosen.every((picked, i) => i === this.giftLengthIndex || picked == null || variant.options[i] === picked)
        )
        .reduce((cheapest, variant) => (!cheapest || variant.price < cheapest.price ? variant : cheapest), null);
    }

    boxCents() {
      const variant = this.displayVariant;
      if (!variant) return 0;
      if (this.isGift) return this.selectedPlan === 'subscription' ? Math.round(variant.price / this.months) : variant.price;
      if (this.selectedPlan === 'subscription' && this.plan) return toCents(this.plan.discount);
      return variant.price;
    }

    renderCta() {
      const variant = this.variant;

      if (!variant || this.missingOptionGroups().length) {
        // Left clickable while the box is unfinished, so it can point at what's still missing.
        this.cta.disabled = this.busy;
        this.ctaLabel.textContent = this.dataset.ctaLabel;
        this.ctaPrice.textContent = '';
        return;
      }

      const isSubscription = !this.isGift && this.selectedPlan === 'subscription';
      const soldOut = !variant.available;
      const waitingOnPlan = isSubscription && !this.plan;

      this.cta.disabled = soldOut || waitingOnPlan || this.busy;
      this.ctaLabel.textContent = soldOut ? this.dataset.soldOutLabel : this.dataset.ctaLabel;

      if (soldOut || waitingOnPlan) {
        this.ctaPrice.textContent = '';
        return;
      }
      const cents = isSubscription ? toCents(this.plan.discount) * this.months : variant.price;
      this.ctaPrice.textContent = `· ${this.formatMoney(cents)}`;
    }

    // Title of the chosen plan card: GoSimple's card once the widget is in charge, otherwise ours.
    selectedPlanTitle() {
      if (this.gosimpleReady) {
        const input = this.gsWidget.querySelector('.gosimple-layout:not([hidden]) input[type="radio"]:checked');
        const title = input && input.closest('label') && input.closest('label').querySelector('strong');
        return title ? title.textContent.trim() : '';
      }
      const input = this.querySelector('[data-plan-input]:checked');
      return input ? input.closest('.sub-pdp__plan').querySelector('[data-plan-title]').textContent.trim() : '';
    }

    checkPlan(value) {
      const input = this.querySelector(`[data-plan-input][value="${value}"]`);
      if (input && !input.disabled) input.checked = true;
    }

    syncModalRows() {
      const format = normalise(this.checkedValue('format'));
      this.querySelectorAll('.sub-pdp__modal [data-format]').forEach((row) => {
        const rowFormat = normalise(row.dataset.format);
        row.hidden = Boolean(rowFormat && format && rowFormat !== format);
      });

      // The note before "How many should I order?" follows the format: bag weight for beans, pod count for pods.
      const note = this.querySelector('[data-size-note]');
      if (note) {
        const keyword = normalise(this.dataset.podsKeyword || 'pod');
        const isPods = Boolean(keyword && format.includes(keyword));
        note.textContent = (isPods ? note.dataset.pods : note.dataset.beans) || '';
        note.hidden = !note.textContent;
      }
    }

    showError(message) {
      if (this.errorEl) this.errorEl.textContent = message || '';
    }

    setBusy(busy) {
      this.busy = busy;
      this.cta.setAttribute('aria-busy', String(busy));
      this.renderCta();
      this.renderNudge();
    }

    // Money with a whole rand amount shown without the ",00" cents (gift prices are round; "R 1,575" not "R 1,575.00").
    moneyNoZeros(cents) {
      return this.formatMoney(cents).replace(/[.,]00(?=\D*$)/, '');
    }

    formatMoney(cents) {
      const format = this.product.moneyFormat || '{{amount}}';
      const amount = cents / 100;
      const delimit = (decimals, thousands, decimal) => {
        const [whole, fraction] = amount.toFixed(decimals).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
      };
      return tightMoney(format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return delimit(0, ',', '.');
          case 'amount_with_comma_separator':
            return delimit(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return delimit(0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return delimit(2, "'", '.');
          default:
            return delimit(2, ',', '.');
        }
      }));
    }

    // The day number is built by hand because some locales (en-ZA included) zero-pad it, e.g. "03 Oct".
    formatDate(ms, options = { day: 'numeric', month: 'short' }) {
      const { day, ...rest } = options;
      const dayText = day ? String(new Date(ms + SAST_OFFSET).getUTCDate()) : '';
      if (!Object.keys(rest).length) return dayText;

      let restText;
      try {
        restText = new Intl.DateTimeFormat(this.dataset.locale, { timeZone: 'Africa/Johannesburg', ...rest }).format(ms);
      } catch (error) {
        restText = new Intl.DateTimeFormat('en-ZA', { timeZone: 'Africa/Johannesburg', ...rest }).format(ms);
      }
      return day ? `${dayText} ${restText}` : restText;
    }

    /* The box that fills as you choose */

    initCrate() {
      this.crate = this.querySelector('[data-crate]');
      if (!this.crate) return;

      const sizes = [...this.querySelectorAll('[data-option-role="size"] [data-option-input]')];
      const maxUnits = Math.min(6, Math.max(0, ...sizes.map((input) => parseInt(input.value, 10) || 0)));
      this.crate.closest('[data-crate-row]').hidden = !maxUnits;
      if (!maxUnits) {
        this.crate = null;
        return;
      }

      const lane = this.crate.querySelector('[data-crate-items]');
      lane.style.setProperty('--slots', maxUnits);
      for (let i = 0; i < maxUnits; i += 1) {
        const item = document.createElement('span');
        item.className = 'sub-pdp__crate-item';
        item.style.setProperty('--tilt', `${i % 2 ? 2.5 : -3}deg`);
        lane.appendChild(item);
      }
      this.crateItems = [...lane.children];
      this.crateUnits = 0;
      this.cupRow = this.querySelector('[data-cup-row]');
      this.cupPrice = this.querySelector('[data-cup-price]');
    }

    renderCrate() {
      if (!this.crate) return;

      const sizeValue = this.checkedValue('size');
      const units = Math.min(this.crateItems.length, parseInt(sizeValue, 10) || 0);
      const formatValue = this.checkedValue('format');
      const keyword = normalise(this.crate.dataset.podsKeyword || 'pod');
      const format = keyword && normalise(formatValue).includes(keyword) ? 'pods' : 'beans';
      const repack = this.crate.dataset.format !== format && this.crateUnits > 0;
      const previous = repack ? 0 : this.crateUnits;

      if (repack) {
        // Snap empty without transitions, then refill, so a format change reads as a fresh pack.
        this.crate.classList.add('is-resetting');
        this.crateItems.forEach((item) => item.classList.remove('is-in'));
        void this.crate.offsetWidth;
        this.crate.classList.remove('is-resetting');
      }

      this.crate.dataset.format = format;
      this.crateItems.forEach((item, i) => {
        item.style.setProperty('--delay', `${Math.max(0, i - previous) * 80}ms`);
        item.classList.toggle('is-in', i < units);
      });
      this.crateUnits = units;

      const picked = [formatValue, this.checkedValue('grind')].filter(Boolean);
      const label = this.crate.querySelector('[data-crate-label]');
      if (label) label.textContent = sizeValue || label.dataset.default || '';
      this.querySelector('[data-crate-title]').textContent = sizeValue
        ? this.crate.dataset.title || sizeValue
        : this.crate.dataset.emptyTitle || '';
      this.querySelector('[data-crate-sub]').textContent = picked.length ? picked.join(' · ') : this.crate.dataset.emptyText || '';

      const cupsPerUnit = Number(format === 'pods' ? this.crate.dataset.cupsPouch : this.crate.dataset.cupsBag) || 0;
      const cups = units * cupsPerUnit;
      const perBox = this.plansLoading && this.selectedPlan === 'subscription' ? 0 : this.boxCents();
      this.cupRow.hidden = !(cups && perBox);
      if (cups && perBox) this.cupPrice.textContent = this.formatMoney(Math.round(perBox / cups));
    }

    /* Next box timeline */

    initTimeline() {
      this.timeline = this.querySelector('[data-timeline]');
      if (!this.timeline) return;
      this.renderTimeline();
      // Minute precision is enough; a seconds ticker reads as pressure, not information.
      this.timelineTimer = window.setInterval(() => this.renderTimeline(), 30000);
    }

    renderTimeline() {
      const timeline = this.timeline;
      const cycle = nextCycle(
        Date.now(),
        Number(timeline.dataset.billDay) || 28,
        Number(timeline.dataset.shipDay) || 1,
        Number(timeline.dataset.arrivalDays) || 0
      );
      this.cycle = cycle;
      if (!cycle) {
        timeline.hidden = true;
        return;
      }

      const sameMonth = this.formatDate(cycle.ships, { month: 'short' }) === this.formatDate(cycle.arrives, { month: 'short' });
      const door =
        cycle.arrives === cycle.ships
          ? this.formatDate(cycle.ships)
          : `${sameMonth ? this.formatDate(cycle.ships, { day: 'numeric' }) : this.formatDate(cycle.ships)}–${this.formatDate(cycle.arrives)}`;

      timeline.querySelector('[data-step-date="order"]').textContent = this.formatDate(cycle.orderBy);
      timeline.querySelector('[data-step-date="roasted"]').textContent = this.formatDate(cycle.roasted);
      timeline.querySelector('[data-step-date="door"]').textContent = door;

      const remaining = Math.max(0, cycle.closes - Date.now());
      const days = Math.floor(remaining / DAY);
      const hours = Math.floor((remaining % DAY) / HOUR);
      const minutes = Math.floor((remaining % HOUR) / 60000);
      const parts = days > 0 ? [[days, 'd', 'days'], [hours, 'h', 'hours']] : [[hours, 'h', 'hours'], [minutes, 'm', 'minutes']];

      const countdown = timeline.querySelector('[data-countdown]');
      countdown.replaceChildren(
        ...parts.flatMap(([value, unit], i) => {
          const number = document.createElement('span');
          number.className = 'sub-pdp__countdown-num';
          number.textContent = String(value);
          const unitEl = document.createElement('span');
          unitEl.className = 'sub-pdp__countdown-unit';
          unitEl.textContent = unit;
          return i ? [document.createTextNode(' '), number, unitEl] : [number, unitEl];
        })
      );
      countdown.setAttribute('aria-label', parts.map(([value, , word]) => `${value} ${word}`).join(' '));
      timeline.hidden = false;
    }

    /* Gift message */

    renderGiftCount() {
      if (!this.giftMessage) return;
      const count = this.querySelector('[data-gift-count]');
      if (count) count.textContent = `${this.giftMessage.value.length}/${this.giftMessage.maxLength}`;
    }

    /* Cart */

    async onSubmit(event) {
      event.preventDefault();
      if (this.cta.disabled) return;
      if (!this.variant || this.missingOptionGroups().length) {
        this.promptMissing();
        return;
      }

      const isSubscription = !this.isGift && this.selectedPlan === 'subscription';
      const plan = this.plan;
      const properties = {};

      this.setBusy(true);
      this.showError('');

      try {
        if (isSubscription) {
          await this.checkFrequency(plan);
          // The same properties hw-subs.js sends, so GoSimple's cart check and checkout read this line identically.
          Object.assign(properties, {
            Subscription: plan.subtype,
            Description: plan.desc,
            actualprice: plan.actual,
            discountprice: plan.discount,
            subscode: plan.frid,
          });
        }

        // Grind is saved on the order line when it isn't a variant option (the gift box).
        const grindGroup = this.querySelector('[data-property-group]:not([hidden])');
        const grindInput = grindGroup && grindGroup.querySelector('[data-option-input]:checked');
        if (grindInput) properties[grindGroup.dataset.property || 'Grind'] = grindInput.value;

        if (this.giftMessage && !this.giftMessage.disabled && this.giftMessage.value.trim()) {
          properties[this.giftMessage.dataset.property || 'Gift message'] = this.giftMessage.value.trim();
        }

        await this.addToCart(properties, isSubscription);
      } catch (error) {
        this.showError(error.userMessage || this.dataset.errorGeneric);
      } finally {
        this.setBusy(false);
      }
    }

    async checkFrequency(plan) {
      const cart = await fetch(`${this.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((response) =>
        response.json()
      );
      const response = await fetch(CHECKFREQ_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          myshopifyDomain: btoa(window.Shopify.shop),
          product: plan.frid,
          cart_token: cart.token,
        }),
      });
      if (!checkfreqAllowed(await response.text())) throw userError(this.dataset.errorMixed);
    }

    async addToCart(properties, isSubscription) {
      const cartDrawer = document.querySelector('cart-drawer');
      const hasDrawer = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function';
      const usePopup = this.dataset.addedPopup === 'true' && this.addedDialog;
      const body = { items: [{ id: this.variant.id, quantity: 1, properties }] };

      if (hasDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${this.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw userError(result.description || result.message || this.dataset.errorGeneric);
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'subscription-product',
          productVariantId: this.variant.id,
          cartData: result,
        });
      }

      this.retireNudge();

      if (usePopup) {
        if (hasDrawer) this.refreshCartDrawer(cartDrawer, result);
        this.showAdded(isSubscription);
      } else if (hasDrawer) {
        cartDrawer.renderContents({ ...result, id: this.variant.id });
      } else {
        window.location.href = this.routes.cart;
      }
    }

    // Mirrors CartDrawer.renderContents without opening the drawer, so the pop-up can take the moment.
    refreshCartDrawer(drawer, result) {
      drawer.getSectionsToRender().forEach((section) => {
        const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        const html = result.sections && result.sections[section.id];
        if (target && html) target.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
      });
      drawer.classList.remove('is-empty');
      const inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
    }

    showAdded(isSubscription) {
      const dialog = this.addedDialog;
      const visual = dialog.querySelector('[data-added-visual]');

      if (this.crate && this.crateUnits) {
        const packed = this.crate.cloneNode(true);
        packed.classList.add('is-packed');
        visual.replaceChildren(packed);
      } else {
        visual.replaceChildren(dialog.querySelector('[data-added-image]').content.cloneNode(true));
      }

      const options = [this.checkedValue('size'), this.checkedValue('format'), this.checkedValue('grind')].filter(Boolean);
      dialog.querySelector('[data-added-summary]').textContent = options.length ? options.join(' · ') : this.dataset.productTitle;

      const planTitle = this.selectedPlanTitle();
      const price = this.ctaPrice.textContent.replace(/^·\s*/, '');
      const termInput = this.isGift && this.selectedPlan === 'subscription' ? this.querySelector('[data-term-input]:checked') : null;
      const term = termInput ? termInput.value : '';
      dialog.querySelector('[data-added-plan]').textContent = [planTitle, term, price].filter(Boolean).join(' · ');

      const ship = dialog.querySelector('[data-added-ship]');
      const template = isSubscription ? ship.dataset.templateSubscription : ship.dataset.templateOneoff;
      ship.hidden = !(this.cycle && template);
      if (this.cycle && template) {
        ship.textContent = template.replace('[date]', this.formatDate(this.cycle.ships, { day: 'numeric', month: 'long' }));
      }

      if (!dialog.open) dialog.showModal();
    }

    viewCart() {
      this.addedDialog.close();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(this.cta);
      } else {
        window.location.href = this.routes.cart;
      }
    }

    /* Scroll reminder */

    initNudge() {
      if (!this.nudge) return;
      try {
        if (window.sessionStorage.getItem('fwks-nudge-dismissed') === '1') return;
      } catch (error) {
        // Storage can be blocked, in which case the reminder simply comes back next visit.
      }

      this.nudge.hidden = false;
      this.nudge.inert = true;

      this.nudge.querySelector('[data-nudge-close]').addEventListener('click', () => {
        this.retireNudge();
        try {
          window.sessionStorage.setItem('fwks-nudge-dismissed', '1');
        } catch (error) {
          // See above.
        }
      });
      this.nudge.querySelector('[data-nudge-cta]').addEventListener('click', () => this.onNudgeAction());

      // A scroll check rather than IntersectionObserver: a quick scroll can jump clean past the
      // 44px button without it ever registering as intersecting, so the card would never appear.
      this.onNudgeScroll = () => {
        if (this.nudgeFrame) return;
        this.nudgeFrame = window.requestAnimationFrame(() => {
          this.nudgeFrame = null;
          this.setNudgeVisible(this.activeCta.getBoundingClientRect().bottom < 0);
        });
      };
      window.addEventListener('scroll', this.onNudgeScroll, { passive: true });
      this.onNudgeScroll();
    }

    onNudgeAction() {
      if (this.missingOptionGroups().length) {
        this.promptMissing();
        return;
      }
      if (this.gosimpleReady) {
        const button = this.activeCta;
        // Sold out: bring the button into view rather than clicking a disabled one.
        if (button.disabled || this.gsWidget.dataset.soldOut === 'true') {
          button.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
        } else {
          button.click();
        }
        return;
      }
      if (this.cta.disabled) {
        this.cta.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      this.form.requestSubmit();
    }

    promptMissing() {
      const missing = this.missingOptionGroups();
      const first = missing.find((group) => !group.hidden);
      if (!first) return;

      this.showError((this.dataset.errorIncomplete || '').replace('[options]', this.optionNames(missing)));
      first.classList.add('is-missing');
      first.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
      const input = first.querySelector('.sub-pdp__pill:not([hidden]) [data-option-input]');
      if (input) input.focus({ preventScroll: true });
    }

    // Still needs a pick: open with nothing chosen, or waiting on a group above it.
    missingOptionGroups() {
      return [...this.querySelectorAll('[data-option-group], [data-property-group]')].filter(
        (group) => group.hasAttribute('data-pending') || (!group.hidden && !group.querySelector('[data-option-input]:checked'))
      );
    }

    optionNames(groups) {
      const names = groups.map((group) => (group.dataset.optionName || group.dataset.optionRole).toLowerCase());
      return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names.join('');
    }

    setNudgeVisible(visible) {
      if (!this.nudge || this.nudgeRetired) return;
      this.nudge.classList.toggle('is-visible', visible);
      this.nudge.inert = !visible;
    }

    retireNudge() {
      if (!this.nudge) return;
      this.setNudgeVisible(false);
      this.nudgeRetired = true;
      if (this.onNudgeScroll) window.removeEventListener('scroll', this.onNudgeScroll);
    }

    renderNudge() {
      if (!this.nudge) return;
      const data = this.nudge.dataset;
      const missing = this.missingOptionGroups();
      const ready = !missing.length && Boolean(this.variant);

      this.nudge.classList.toggle('is-ready', ready);
      this.nudge.querySelector('[data-nudge-title]').textContent = ready ? data.readyHeading : data.incompleteHeading;
      this.nudge.querySelector('[data-nudge-label]').textContent = ready ? data.readyCta : data.incompleteCta;

      const summary = this.nudge.querySelector('[data-nudge-summary]');
      const price = this.nudge.querySelector('[data-nudge-price]');
      if (ready) {
        const parts = [this.checkedValue('size'), this.checkedValue('format')].filter(Boolean);
        const planTitle = this.selectedPlanTitle();
        if (planTitle) parts.push(planTitle);
        const termInput = this.isGift && this.selectedPlan === 'subscription' ? this.querySelector('[data-term-input]:checked') : null;
        if (termInput) parts.push(termInput.value);
        summary.textContent = parts.join(' · ');
        price.textContent = this.ctaPrice.textContent.replace(/^·\s*/, '');
      } else {
        summary.textContent = (data.todo || '[options]').replace('[options]', this.optionNames(missing));
        price.textContent = '';
      }
    }

    /* Gallery */

    initGallery() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.slideIndex = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showSlide(Number(thumb.dataset.thumb)));
      });

      this.galleryPrev = this.querySelector('[data-gallery-prev]');
      this.galleryNext = this.querySelector('[data-gallery-next]');
      if (this.galleryPrev) this.galleryPrev.addEventListener('click', () => this.showSlide(this.slideIndex - 1));
      if (this.galleryNext) this.galleryNext.addEventListener('click', () => this.showSlide(this.slideIndex + 1));

      const stage = this.querySelector('[data-stage]');
      if (!stage || this.slides.length < 2) return;

      let startX = null;
      stage.addEventListener('touchstart', (event) => {
        startX = event.touches[0].clientX;
      }, { passive: true });
      stage.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) this.showSlide(this.slideIndex + (delta < 0 ? 1 : -1));
        startX = null;
      });
    }

    // Stops at the first and last image (no wrap-around), like the theme's main product gallery.
    showSlide(index) {
      if (this.slides.length < 2 || Number.isNaN(index)) return;
      const last = this.slides.length - 1;
      this.slideIndex = Math.min(last, Math.max(0, index));
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.slideIndex;
      });
      this.thumbs.forEach((thumb, i) => {
        const active = i === this.slideIndex;
        thumb.setAttribute('aria-current', String(active));
        // Centres the active thumbnail in the strip; scrollIntoView would scroll the whole page too.
        if (active) {
          const strip = thumb.parentElement.getBoundingClientRect();
          const rect = thumb.getBoundingClientRect();
          thumb.parentElement.scrollBy({
            top: rect.top - strip.top - (strip.height - rect.height) / 2,
            left: rect.left - strip.left - (strip.width - rect.width) / 2,
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
          });
        }
      });
      if (this.galleryPrev) this.galleryPrev.disabled = this.slideIndex === 0;
      if (this.galleryNext) this.galleryNext.disabled = this.slideIndex === last;
    }
  }

  customElements.define('subscription-product', SubscriptionProduct);

  // Blocks inside closed dialogs and accordions are invisible in the theme editor until opened.
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const dialog = target.closest('.sub-pdp__modal');
    if (dialog && !dialog.open) dialog.showModal();
    const details = target.closest('details');
    if (details) details.open = true;
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const dialog = target.closest('.sub-pdp__modal');
    if (dialog && dialog.open) dialog.close();
  });
})();
